import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import FocusedRowChip from '../common/FocusedRowChip';
import { useFocusedRow } from '../../hooks/useFocusedRow';
import {
    BreakdownRequestDTO, REQUEST_STATUSES, TechnicianDTO, WORK_ORDER_PRIORITIES, WorkOrderPriority, mntLabel,
} from '../../interfaces/maintenance';
import { REQUEST_STATUS_COLOR, cell, fmtDateTime } from './woCommon';

type Action = { kind: 'accept' | 'reject' | 'merge'; row: BreakdownRequestDTO } | null;

/** Triage inbox: accept a report into a work order, reject it with a reason, or merge it into another. */
const BreakdownRequestInbox: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const canAccept = useAuth().hasPermission(PERMISSIONS.MAINTENANCE_WO_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<BreakdownRequestDTO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [status, setStatus] = useState('SUBMITTED');
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState<Action>(null);
    const [technicians, setTechnicians] = useState<TechnicianDTO[]>([]);
    const [candidates, setCandidates] = useState<BreakdownRequestDTO[]>([]);
    const [priority, setPriority] = useState<WorkOrderPriority | ''>('');
    const [assignTo, setAssignTo] = useState('');
    const [title, setTitle] = useState('');
    const [reason, setReason] = useState('');
    const [targetId, setTargetId] = useState('');
    const [saving, setSaving] = useState(false);
    const { focusId, clear: clearFocus } = useFocusedRow();

    const load = useCallback(async () => {
        try {
            setLoading(true);
            if (focusId) {
                setRows([await MaintenanceService.getRequest(focusId)]);
                setTotal(1);
                return;
            }
            const res = await MaintenanceService.listRequests(institutionId, status, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load breakdown requests');
        } finally {
            setLoading(false);
        }
    }, [institutionId, status, page, size, focusId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const openAction = (kind: 'accept' | 'reject' | 'merge', row: BreakdownRequestDTO) => {
        setPriority('');
        setAssignTo(''); setTitle(''); setReason(''); setTargetId('');
        setAction({ kind, row });
        if (kind === 'accept') {
            MaintenanceService.listTechnicians(institutionId).then(setTechnicians).catch(() => setTechnicians([]));
        }
        if (kind === 'merge') {
            Promise.all([
                MaintenanceService.listRequests(institutionId, 'SUBMITTED', 0, 100),
                MaintenanceService.listRequests(institutionId, 'ACCEPTED', 0, 100),
            ])
                .then(([a, b]) => setCandidates([...a.content, ...b.content].filter(c => c.id !== row.id)))
                .catch(() => setCandidates([]));
        }
    };

    const submit = async () => {
        if (!action) return;
        try {
            setSaving(true);
            if (action.kind === 'accept') {
                await MaintenanceService.acceptRequest(action.row.id, {
                    priority: priority || undefined, assignTo: assignTo || undefined, title: title.trim() || undefined,
                });
                toast.showSuccess('Request accepted, work order created');
            } else if (action.kind === 'reject') {
                await MaintenanceService.rejectRequest(action.row.id, reason.trim());
                toast.showSuccess('Request rejected');
            } else {
                await MaintenanceService.mergeRequest(action.row.id, targetId);
                toast.showSuccess('Request merged');
            }
            setAction(null);
            load();
        } catch (e) {
            toast.showApiError(e, 'The action failed');
        } finally {
            setSaving(false);
        }
    };

    const canSubmit = action?.kind === 'reject' ? reason.trim().length > 0 : action?.kind === 'merge' ? targetId !== '' : true;

    return (
        <Box sx={{ p: 2 }} data-help-page="maint.requests.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Breakdown requests</Typography>
            <FocusedRowChip focusId={focusId} noun="request" onClear={clearFocus} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="maint.requests.filter">
                <TextField
                    select size="small" label="Status" value={status} sx={cell(180)} data-help="maint.requests.filter.status"
                    onChange={e => { setStatus(e.target.value); setPage(0); }}
                >
                    {REQUEST_STATUSES.map(s => <MenuItem key={s} value={s}>{mntLabel(s)}</MenuItem>)}
                </TextField>
            </Box>
            <Paper data-help-section="maint.requests.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="maint.requests.col.no">No.</TableCell>
                                    <TableCell data-help="maint.requests.col.machine">Machine / place</TableCell>
                                    <TableCell data-help="maint.requests.col.problem">Problem</TableCell>
                                    <TableCell data-help="maint.requests.col.by">Reported by</TableCell>
                                    <TableCell data-help="maint.requests.col.when">Reported</TableCell>
                                    <TableCell data-help="maint.requests.col.status">Status</TableCell>
                                    <TableCell data-help="maint.requests.col.actions" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center">No breakdown requests</TableCell></TableRow>
                                )}
                                {rows.map(r => (
                                    <TableRow key={r.id} hover>
                                        <TableCell>{r.requestNo}</TableCell>
                                        <TableCell>
                                            {r.equipmentCode ? `${r.equipmentCode} ${r.equipmentName ?? ''}` : r.locationName ?? '-'}
                                            {r.machineStopped && <Chip size="small" color="error" label="Stopped" sx={{ ml: 1 }} />}
                                            {r.possibleDuplicateOf && <Chip size="small" color="warning" label="Possible duplicate" sx={{ ml: 1 }} />}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 280 }}>{r.description}</TableCell>
                                        <TableCell>{r.requestedByName ?? '-'}</TableCell>
                                        <TableCell>{fmtDateTime(r.createdAt)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={mntLabel(r.status)} color={REQUEST_STATUS_COLOR[r.status]} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {r.status === 'SUBMITTED' && (
                                                <>
                                                    {canAccept && <Button data-help="maint.requests.accept" size="small" onClick={() => openAction('accept', r)}>Accept</Button>}
                                                    <Button data-help="maint.requests.merge" size="small" onClick={() => openAction('merge', r)}>Merge</Button>
                                                    <Button data-help="maint.requests.reject" size="small" color="error" onClick={() => openAction('reject', r)}>Reject</Button>
                                                </>
                                            )}
                                            {r.workOrderId && (
                                                <Button data-help="maint.requests.action.wo" size="small" onClick={() => navigate(`/maintenance/work-orders/${r.workOrderId}`)}>Work order</Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    component="div" count={total} page={page} rowsPerPage={size}
                    rowsPerPageOptions={[10, 20, 50]}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            <Dialog open={action !== null} onClose={() => setAction(null)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'maint.requestAction.page' } as any}>
                <DialogTitle>
                    {action?.kind === 'accept' && `Accept ${action.row.requestNo}`}
                    {action?.kind === 'reject' && `Reject ${action.row.requestNo}`}
                    {action?.kind === 'merge' && `Merge ${action.row.requestNo} into another request`}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {action?.kind === 'accept' && (
                            <>
                                <TextField
                                    select size="small" label="Priority" value={priority} data-help="maint.requestAction.field.priority"
                                    onChange={e => setPriority(e.target.value as WorkOrderPriority | '')}
                                >
                                    <MenuItem value="">Default (by criticality)</MenuItem>
                                    {WORK_ORDER_PRIORITIES.map(p => <MenuItem key={p} value={p}>{mntLabel(p)}</MenuItem>)}
                                </TextField>
                                <SearchableSelect
                                    options={technicians} value={assignTo} size="small" label="Assign to (optional)" data-help="maint.requestAction.field.assignTo"
                                    getOptionId={t => t.userId} getOptionLabel={t => t.name}
                                    onChange={id => setAssignTo(id)}
                                />
                                <TextField
                                    size="small" label="Work order title (optional)" value={title} data-help="maint.requestAction.field.title"
                                    onChange={e => setTitle(e.target.value)}
                                />
                            </>
                        )}
                        {action?.kind === 'reject' && (
                            <TextField
                                size="small" label="Reason" value={reason} multiline minRows={2} required data-help="maint.requestAction.field.reason"
                                onChange={e => setReason(e.target.value)}
                            />
                        )}
                        {action?.kind === 'merge' && (
                            <SearchableSelect
                                options={candidates} value={targetId} size="small" label="Merge into" data-help="maint.requestAction.field.mergeInto"
                                getOptionLabel={c => `${c.requestNo} - ${c.equipmentCode ?? c.locationName ?? ''} ${c.description.slice(0, 40)}`}
                                onChange={id => setTargetId(id)}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAction(null)} data-help="maint.requestAction.cancel">Cancel</Button>
                    <Button variant="contained" disabled={saving || !canSubmit} onClick={submit} data-help="maint.requestAction.confirm">Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BreakdownRequestInbox;
